import React, { useMemo, useState } from "react";
import Container from "../ui/Container";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { BRAND } from "../../lib/config";
import { WEB_DISCOUNTS, WEB_PLANS } from "../../lib/webHosting";
import { Calendar, Tag, ArrowUpRight } from "lucide-react";

function moneyGBP(n, opts = {}) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: opts.max0 ? 0 : 2,
  }).format(n);
}

function CyclePill({ active, onClick, label, sublabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex-1 rounded-2xl border px-4 py-3 text-left transition",
        active
          ? "border-cyan-300/40 bg-cyan-500/10"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]",
      ].join(" ")}
    >
      <div className="text-sm font-semibold text-white">{label}</div>
      {sublabel ? <div className="mt-1 text-xs text-white/55">{sublabel}</div> : null}
    </button>
  );
}

function Spec({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <div className="text-sm text-white/65">{k}</div>
      <div className="text-sm font-semibold text-white">{v}</div>
    </div>
  );
}

export default function WebHostingPlans() {
  const [cycleId, setCycleId] = useState("m");
  const cycle = useMemo(
    () => WEB_DISCOUNTS.find((c) => c.id === cycleId) || WEB_DISCOUNTS[0],
    [cycleId]
  );

  return (
    <section id="plans" className="bg-[#070A0F] py-20 scroll-mt-20">
      <Container>
        <div className="mx-auto max-w-4xl text-center">
          <div className="text-xs font-semibold tracking-wider text-white/50">WEB PLANS</div>
          <h2 className="mt-3 text-balance text-4xl font-semibold leading-[0.95] tracking-tight text-white sm:text-5xl">
            Clean plans.
            <span className="block">
              No upsell maze<span className="text-cyan-300">.</span>
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-white/70 sm:text-lg">
            Monthly pricing is the anchor. Save with 3/6/12 month prepay.
          </p>
        </div>

        {/* Billing cycle */}
        <div className="mt-10 mx-auto max-w-4xl rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <Calendar className="h-4 w-4 text-cyan-300" />
              Billing cycle
            </div>

            {cycle.months > 1 ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-xs font-semibold text-white/80">
                <Tag className="h-4 w-4 text-cyan-300" />
                Save {(cycle.discount * 100).toFixed(0)}% with {cycle.months} months
              </div>
            ) : (
              <div className="text-xs text-white/45">Pay as you go</div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {WEB_DISCOUNTS.map((c) => (
              <CyclePill
                key={c.id}
                active={cycleId === c.id}
                onClick={() => setCycleId(c.id)}
                label={c.label}
                sublabel={c.sublabel}
              />
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {WEB_PLANS.map((p) => {
            const original = p.monthly * cycle.months;
            const dueToday = original * (1 - cycle.discount);
            const savings = original - dueToday;

            return (
              <Card key={p.id} className="p-7 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-lg font-semibold text-white">
                      {p.name}<span className="text-cyan-300">.</span>
                    </div>
                    {p.badge ? (
                      <div className="mt-2 inline-flex rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-xs font-semibold text-white/70">
                        {p.badge}
                      </div>
                    ) : null}
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-white/50">From</div>
                    <div className="mt-1 text-2xl font-semibold text-white">
                      {moneyGBP(p.monthly, { max0: true })}
                      <span className="text-white/40 text-base font-medium">/mo</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <Spec k="Sites" v={p.specs.sites} />
                  <Spec k="Storage" v={p.specs.storage} />
                  <Spec k="Bandwidth" v={p.specs.bandwidth} />
                  <Spec k="SSL" v={p.specs.ssl} />
                  <Spec k="Backups" v={p.specs.backups} />
                  <Spec k="Email" v={p.specs.email} />
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-xs font-semibold tracking-wider text-white/50">TOTAL</div>

                  <div className="mt-2 text-sm text-white/70">
                    <span className="text-white/50">Due today:</span>{" "}
                    <span className="font-semibold text-white">
                      {cycle.months === 1
                        ? `${moneyGBP(p.monthly, { max0: true })}/mo`
                        : `${moneyGBP(dueToday, { max0: true })} for ${cycle.months} months`}
                    </span>
                    {cycle.months > 1 && (
                      <span className="ml-2 text-xs text-white/45">
                        (Was: {moneyGBP(original, { max0: true })})
                      </span>
                    )}
                  </div>

                  {cycle.months > 1 && (
                    <div className="mt-2 text-xs text-white/55">
                      You save {moneyGBP(savings)} over {cycle.months} months.
                    </div>
                  )}

                  <div className="mt-4 flex flex-col gap-3">
                    <Button href={BRAND.deployUrl + "web-hosting/" + p.id} className="w-full justify-center">
                      Get Web Hosting <ArrowUpRight className="h-4 w-4 opacity-80" />
                    </Button>
                    <Button variant="secondary" href={BRAND.supportUrl} className="w-full justify-center">
                      Ask Support
                    </Button>
                  </div>

                  <p className="mt-3 text-[11px] leading-5 text-white/50">
                    *Unmetered subject to fair use. We’ll help you choose the right plan.
                  </p>
                </div>

                <div className="mt-6">
                  <div className="text-xs font-semibold tracking-wider text-white/50">IDEAL FOR</div>
                  <ul className="mt-3 space-y-2 text-sm text-white/65">
                    {p.idealFor.map((x) => (
                      <li key={x} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
